import React, { useRef, useState } from 'react';
import { useScheduler } from './SchedulerContext';
import { getDaysInView, DAY_WIDTH, ROW_HEIGHT, getDateFromPixel } from '../utils/dateUtils';
import { EventItem } from './EventItem';
import { format, isSameDay, isWeekend, isWithinInterval } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ShowAssignmentModal, ShowTimeOffConfirmationModal, UpdateShowMilestone } from './SchedulerSlice';
import { formatDateWithDateAndMonthName } from '../../common/timeLiveFunction';
import { calculateRowHeights } from '../utils/rowHeightCalculator';
import { ShowToast } from '../../common/Toastr';

const getOverlappingEvents = (events: any[], startDate: Date, viewMode: string) => {
  const eventsByResource = new Map<string, any[]>();

  events.forEach(event => {
    if (event.types !== 'task' && event.types !== 'taskTeam') {
      return;
    }

    if (!eventsByResource.has(event.resourceId)) {
      eventsByResource.set(event.resourceId, []);
    }
    eventsByResource.get(event.resourceId)!.push(event);
  });

  const positionedEvents = new Map<string, any>();

  eventsByResource.forEach((resourceEvents, resourceId) => {
    const sorted = [...resourceEvents].sort((a, b) =>
      a.start.getTime() - b.start.getTime()
    );

    sorted.forEach(event => {
      const overlapping = sorted.filter(e => {
        if (e.id === event.id) return false;

        const eventStart = event.start.getTime();
        const eventEnd = event.end.getTime();
        const eStart = e.start.getTime();
        const eEnd = e.end.getTime();

        return (eventStart <= eEnd && eventEnd >= eStart);
      });

      let lane = 0;
      const usedLanes = new Set<number>();

      overlapping.forEach(e => {
        const pos = positionedEvents.get(e.id);
        if (pos) usedLanes.add(pos.lane);
      });

      while (usedLanes.has(lane)) {
        lane++;
      }

      positionedEvents.set(event.id, {
        ...event,
        lane,
        totalLanes: Math.max(lane + 1, overlapping.length + 1)
      });
    });
  });

  return positionedEvents;
};

export const TimelineGrid: React.FC = () => {
  const { flatResources, startDate, endDate, viewMode, events, onMilestoneAdd, UpdateEventResize, onTimeOffClick } = useScheduler();
  const { cultureInfoName } = useAppSelector((state) => state.globalState);
  const { IsList, IsAdd, milestonePermissionsValues } = useAppSelector((state) => state.scheduler);
  // const days = getDaysInView(startDate, viewMode);
  const days = getDaysInView(startDate, viewMode, endDate);
  const containerRef = useRef<HTMLDivElement>(null);
  const totalWidth = days.length * DAY_WIDTH;
  const dispatch = useAppDispatch();
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [hoveredDay, setHoveredDay] = useState<{
    resourceId: string;
    dayIndex: number;
    date: Date;
  } | null>(null);

  const positionedEvents = getOverlappingEvents(events, startDate, viewMode);
  const rowHeights = calculateRowHeights(flatResources, events, positionedEvents);
  const rowPositions: number[] = [];
  let currentTop = 0;
  flatResources.forEach((resource, index) => {
    rowPositions[index] = currentTop;
    currentTop += rowHeights[index];
  });

  const totalHeight = currentTop;
  const hasTimeOffOnDate = (resourceId: string, date: Date) => {
    return events.some(event => {
      if (event.types !== 'timeOff') return false;
      if (event.resourceId !== resourceId && event.parentResourceId !== resourceId) return false;

      return isWithinInterval(date, {
        start: event.start,
        end: event.end
      }) || isSameDay(date, event.start) || isSameDay(date, event.end);
    });
  };

  const handleRowClick = (e: React.MouseEvent, resource: any) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickedDate = getDateFromPixel(clickX, startDate, viewMode);
    const dayIndex = Math.floor(clickX / DAY_WIDTH);
    const clickedDay = days[dayIndex];
    const isWeekendDay = isWeekend(clickedDay);
    if (clickedDay && hasTimeOffOnDate(resource.id, clickedDay) && resource.type != 'team') {
      const timeOffEvent = events.find(event =>
        event.types === 'timeOff' &&
        (event.resourceId === resource.id || event.parentResourceId === resource.id) &&
        (isWithinInterval(clickedDay, { start: event.start, end: event.end }) ||
          isSameDay(clickedDay, event.start) ||
          isSameDay(clickedDay, event.end))
      );

      dispatch(ShowTimeOffConfirmationModal({
        show: true,
        timeOffDate: clickedDay,
        timeOffTypeName: timeOffEvent?.TimeOffTypeName,
        resourceData: resource,
        clickedDate: clickedDay
      }));
      return;
    }

    if (isWeekendDay && resource.type != 'team') {
      if (!resource?.permissionForAdd || !IsAdd) {
        ShowToast('Warning', "You do not have permission to schedule hours.", "warning", "toast-top-right");
        return;
      }
      dispatch(ShowTimeOffConfirmationModal({
        show: true,
        timeOffDate: clickedDay,
        timeOffTypeName: undefined,
        resourceData: resource,
        clickedDate: clickedDay,
        isWeekend: true
      }));
      return;
    }

    if (resource.type === 'project') {
      if (!milestonePermissionsValues?.IsAdd) return;
      if (hoveredDay) {
        onMilestoneAdd?.({ clickedDate: hoveredDay.date, AccountProjectId: resource.id });
        dispatch(UpdateShowMilestone(true));
      }
    } else if ((resource.type === 'person')) {
      
      if (!resource?.permissionForAdd || !IsAdd) {
       ShowToast('Warning', "You do not have permission to schedule hours.", "warning", "toast-top-right");
        return;
      }
      dispatch(ShowAssignmentModal({ flag: true, ShowAssignmentModal: { ...resource, clickedDate } }));
    } else if (resource.type === 'teamProject') {
      if (!resource?.permissionForAdd || !IsAdd) {
        ShowToast('Warning', "You do not have permission to schedule hours.", "warning", "toast-top-right");
        return;
      }
      const employeeName = flatResources?.find((item: any) => item?.id === resource?.parentId)?.name;
      dispatch(ShowAssignmentModal({ flag: true, ShowAssignmentModal: { ...resource, employeeName: employeeName, AccountEmployeeId: resource?.parentId, clickedDate } }));
    }
  };

  const handleProjectHover = (e: React.MouseEvent, resourceId: string) => {
     if (!milestonePermissionsValues?.IsAdd && !milestonePermissionsValues?.IsList) {
      setHoveredDay(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const hoverX = e.clientX - rect.left;

    const dayIndex = Math.floor(hoverX / DAY_WIDTH);
    const hoveredDate = days[dayIndex];

    if (!hoveredDate) return;

    const hasMilestone = events.some(
      ev =>
        ev.types === 'milestone' &&
        ev.resourceId === resourceId &&
        ev.start.toDateString() === hoveredDate.toDateString()
    );

    if (!hasMilestone) {
      setHoveredDay({ resourceId, dayIndex, date: hoveredDate });
    } else {
      setHoveredDay(null);
    }
  };

  const handleProjectLeave = () => {
    setHoveredProject(null);
  };

  const handleTimeOffClick = (resource: any, date: Date, timeOffEvent: any) => {
    if(resource?.type === 'team') return;
      if (!resource?.permissionForAdd || !IsAdd) {
      ShowToast('Warning', "You do not have permission to schedule hours.", "warning", "toast-top-right");
      return;
    }
    dispatch(ShowTimeOffConfirmationModal({
      show: true,
      timeOffDate: date,
      timeOffTypeName: timeOffEvent?.TimeOffTypeName,
      resourceData: resource,
      clickedDate: date
    }));
  };

  return (
    <div
      className="position-relative overflow-hidden"
      style={{ height: flatResources.length * ROW_HEIGHT, width: totalWidth }}
      ref={containerRef}
    >
      {days.map((day, dayIndex) => (
        <div
          key={`border-${dayIndex}`}
          className="position-absolute"
          style={{
            left: dayIndex * DAY_WIDTH,
            top: 0,
            width: DAY_WIDTH,
            height: totalHeight,
            borderRight: day.getDay() === 0 ? '2px dotted #b0b0b0' : '1px solid #dee2e6',
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      ))}
      {flatResources.map((resource, resourceIndex) => {
        if (resource.type === 'add_button') return null;
        const rowTop = rowPositions[resourceIndex];
        const rowHeight = rowHeights[resourceIndex];

        return days?.map((day: any, dayIndex: any) => {
          const isTimeOff = hasTimeOffOnDate(resource.id, day);
          const isWeekendDay = isWeekend(day);
          if (!isTimeOff && !isWeekendDay) return null;

          const timeOffEvent = isTimeOff ? events.find(event =>
            event.types === 'timeOff' &&
            (event.resourceId === resource.id || event.parentResourceId === resource.id) &&
            (isWithinInterval(day, { start: event.start, end: event.end }) ||
              isSameDay(day, event.start) ||
              isSameDay(day, event.end))
          ) : null;

          return (
            <div
              key={`timeoff-${resourceIndex}-${dayIndex}`}
              className="position-absolute"
              data-tooltip-id={isTimeOff ? "scheduler-event-tooltip" : undefined}
              data-tooltip-html={isTimeOff && timeOffEvent ?
                `<div style="line-height: 1.5;">
                <div style="font-weight: bold; margin-bottom: 4px;">${timeOffEvent.TimeOffTypeName || 'Time Off'}</div>
                <div style="font-size: 11px;">${formatDateWithDateAndMonthName(timeOffEvent.start, cultureInfoName.toString())} - ${formatDateWithDateAndMonthName(timeOffEvent.end, cultureInfoName.toString())}</div>
                </div>` : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if ((isTimeOff && timeOffEvent)) {
                  handleTimeOffClick(resource, day, timeOffEvent);
                }
              }}
              style={{
                left: dayIndex * DAY_WIDTH,
                // top: isWeekendDay ? '' : rowTop + 5,
                top: isTimeOff ? rowTop : '',
                width: DAY_WIDTH,
                height: isTimeOff ? rowHeight : '100%',
                // height: isTimeOff ? '37px' : '100%',
                backgroundColor: isTimeOff ? 'gray' : isWeekendDay ? '#f3f4f6' : '#ffffff',
                backgroundImage: isTimeOff ? 'repeating-linear-gradient(145deg, #374151 0, #374151 2px, transparent 2px, transparent 5px)' : 'none',
                // borderBottom: isTimeOff ? '3px solid black' : '',
                borderRight: day.getDay() === 0 ? "2px dotted #b0b0b0" : "1px solid #dee2e6",
                opacity: isTimeOff ? 0.5 : '',
                // pointerEvents: 'auto',
                pointerEvents: isTimeOff ? 'auto' : 'none',
                zIndex: 10
              }}
            />
          );
        });
      })}

      {flatResources.map((resource, i) => {
        if (resource.type === 'add_button') return null;
        const isProject = resource.type === 'project';
        const rowTop = rowPositions[i];
        const rowHeight = rowHeights[i];
        
        return (
          <div
            key={i}
            className="position-absolute w-100 border-bottom"
            style={{ top: rowTop, height: rowHeight, cursor: 'pointer', transition: 'background-color 0.2s ease' }}
            onClick={(e) => handleRowClick(e, resource)}
            onMouseMove={isProject ? (e) => handleProjectHover(e, resource.id) : undefined}
            onMouseLeave={isProject ? handleProjectLeave : undefined}
          >
            {isProject && IsList && milestonePermissionsValues?.IsAdd && milestonePermissionsValues?.IsList && hoveredDay?.resourceId === resource.id && (
              <div
                className="position-absolute bg-secondary bg-opacity-28"
                style={{
                  left: hoveredDay?.dayIndex! * DAY_WIDTH,
                  top: 4,
                  width: DAY_WIDTH,
                  height: '35px',
                  cursor: 'pointer',
                  zIndex: 50,
                  textAlign: 'center'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // if(!milestonePermissionsValues?.IsAdd) return;
                  if (hoveredDay) {
                    onMilestoneAdd?.({
                      clickedDate: hoveredDay.date,
                      AccountProjectId: resource.id
                    });
                    dispatch(UpdateShowMilestone(true));
                  }
                }}
              >
                <span style={{ fontSize: 11 }}>Add Milestone</span>
              </div>
            )}
          </div>
        );
      })}

      {flatResources.map((resource, rowIndex) => {
        const resourceEvents = events.filter(e => {
          const isIdMatch = e.resourceId === resource.id;

          if (isIdMatch && resource.type === 'person') {
            return (e.ProjectId == resource.ProjectId) || e.types === 'timeOff';
          }

          if (isIdMatch && resource.type === 'team') {
            return (e.types === 'allocation' || e.types === 'timeOff') && e.resourceId === resource.id && (!e.parentResourceId || e.parentResourceId === resource.id);
          }

          if (isIdMatch && resource.type === 'teamProject') {
            return (e.types === 'taskTeam' || e.types === 'timeOff') && e.resourceId === resource.id && e.parentResourceId === resource.parentId;
          }

          return isIdMatch;
        });
        const isProject = resource.type === 'project';
        const rowTop = rowPositions[rowIndex];
        const rowHeight = rowHeights[rowIndex];
        return (
          <div
            key={rowIndex}
            className="position-absolute w-100"
            style={{ top: rowTop, height: rowHeight, pointerEvents: 'none' }}
            onMouseMove={isProject ? (e) => handleProjectHover(e, resource.id) : undefined}
            onMouseLeave={isProject ? handleProjectLeave : undefined}
          >
            {resourceEvents.map((event, eventIndex) => {
              const positionedEvent = positionedEvents.get(event.id);
              const lane = (event.types === 'task' || event.types === 'taskTeam') ? (positionedEvent?.lane || 0) : 0;
              const totalLanes = (event.types === 'task' || event.types === 'taskTeam') ? (positionedEvent?.totalLanes || 1) : 1;
              return (
                <div key={eventIndex} style={{ pointerEvents: event.types === 'timeOff' ? 'none' : 'auto' }}>
                  <EventItem event={event} containerRef={containerRef} UpdateEventResize={UpdateEventResize} lane={lane} totalLanes={totalLanes} />
                </div>
              )
            })}
          </div>
        );
      })}
    </div>
  );
};
